<template>
  <router-link
    :to="link"
    class="list-result-style"
    @click.native="savePosition"
  >
    <div
      :class="[
        'list-result-style__tile-img',
        showHeader && 'list-result-style__tile-img-with-header',
      ]"
      :id="resultId"
      :style="`background-image: url(${tileImg}), linear-gradient(${gradient})`"
    >
      <div v-if="showHeader" class="list-result-style__header">
        <span class="list-result-style__header-description">
          Live Bootcamp
        </span>
        <span
          v-if="isContentComingSoon"
          :class="[isContentComingSoon && 'list-result-style__header-status']"
          >Coming Soon</span
        >
      </div>
      <div class="list-result-style__icon-wrapper">
        <PlayArrowIconOutlined
          class="material-icons list-result-style__icon"
        ></PlayArrowIconOutlined>
      </div>
      <div class="list-result-style__progress-bar-wrapper " v-if="itemProgress">
        <PreviewProgressBar
          :value="itemProgress"
          :size="'small'"
        ></PreviewProgressBar>
      </div>
    </div>
    <div class="list-result-style__info">
      <div>
        <div class="list-result-style__top">
          <div class="list-result-style__top-left">
            <template v-if="registered">
              <div class="list-result-style--registered">
                <span>You registered!</span>
              </div>
            </template>
            <template v-if="difficulty !== ''">
              <div class="list-result-style__difficulty">
                <span>{{ difficulty }}</span>
              </div>
            </template>
            <template v-if="displayRunningPill && running">
              <div class="tile__running-pill">
                <RunningPill />
              </div>
            </template>
          </div>
          <template v-if="showAddedDate && course.published_date">
            <AddedDate :date="course.published_date" :limit="7"></AddedDate>
          </template>
        </div>
        <div class="list-result-style__title">
          <span>{{ course.name }}</span>
          <span
            v-if="course.has_sample_content && isFreeTrialUser"
            class="list-result-style__snipe"
          >
            | Included
          </span>
          <span
            v-if="course.status === 'coming soon'"
            class="list-result-style__snipe"
          >
            | Coming Soon
          </span>
        </div>
      </div>
      <div class="list-result-style__bottom">
        <div
          v-tooltip="{
            content: `${getCertificationFullName(course)}`,
            placement: 'top-center',
          }"
          class="list-result-style__certificate bottom-element"
          v-if="shouldDisplayCertificate(course, type)"
        >
          <div
            class="list-result-style__certificate-icon"
            :style="`background:${learningAreaColor}`"
          >
            <CertificateIcon />
          </div>
          <span> {{ getCertificateName(course) }} </span>
        </div>
        <div class="list-result-style__vendor-wrapper bottom-element">
          <span
            :style="`background:${learningAreaColor}`"
            class="list-result-style__vendor-dot"
          ></span>
          <span>{{ learningArea }}</span>
        </div>
        <div
          v-if="displayLabType"
          class="list-result-style__vendor-wrapper bottom-element"
        >
          <span>{{ getLabTypeLabel(course) }}</span>
        </div>
        <div v-if="displayParentCourse">
          <span>COURSE: {{ truncateTitle(courseName) }}</span>
        </div>
        <div
          v-if="!instructorAtTheEnd && displayInstructor"
          class="list-result-style__bottom-instructor bottom-element"
        >
          <span>{{ getInstructorNameText(course) }}</span>
        </div>
        <div
          v-if="showDuration"
          class="list-result-style__bottom-duration-wrapper bottom-element"
        >
          <span class="bottom-duration">{{ itemDuration() }}</span>
        </div>
        <div
          v-if="showLastActivity()"
          class="list-result-style__course-last-activity bottom-element"
        >
          <span class="bottom-lastactivity">
            Active {{ getTimeInDaysAgo() }}
          </span>
        </div>
        <template v-if="instructorAtTheEnd && displayInstructor">
          <div class="list-result-style__bottom-instructor bottom-element">
            <span>{{ getInstructorNameText(course) }}</span>
          </div>
        </template>
        <span v-if="displayVendor">{{ getVendor(course) }}</span>
      </div>
    </div>
  </router-link>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import CONSTANTS from '@/constants'
import AddedDate from '@/components/properties/AddedDate.vue'
import { formatDistance } from 'date-fns'
import PreviewProgressBar from '@/components/properties/PreviewProgressBar.vue'
import {
  getLearningAreaSnipeColor,
  getEntities,
  getLearningArea,
  getCertificateName,
  getCertificationFullName,
  shouldDisplayCertificate,
  saveCurrentElement,
  truncateText,
  duration,
} from '@/helpers'
import PlayArrowIconOutlined from '@/assets/icons/navigation/play_arrow_outlined.svg'
import CertificateIcon from '@/assets/icons/search/certificate.svg'
import RunningPill from '@/components/properties/RunningPill.vue'

export default {
  name: 'ListResult',
  inject: ['getBreakpoint'],
  components: {
    AddedDate,
    PreviewProgressBar,
    PlayArrowIconOutlined: {
      name: 'PlayArrowIconOutlined',
      ...PlayArrowIconOutlined,
    },
    CertificateIcon,
    RunningPill,
  },
  data: () => {
    return {
      getCertificateName,
      getCertificationFullName,
      shouldDisplayCertificate,
      getLearningArea,
    }
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    course: {
      type: Object,
      required: false,
    },
    gradient: {
      type: String,
      required: true,
    },
    tileImg: {
      type: String,
      required: false,
    },
    duration: {
      type: String,
      required: false,
      default: () => '',
    },
    link: {
      type: Object,
      required: true,
    },
    difficulty: {
      type: String,
      required: false,
    },
    isFreeTrialUser: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    progress: {
      type: Number,
      required: false,
      default: () => 0,
    },
    learningArea: {
      type: String,
      required: false,
      default: () => null,
    },
    learningAreaColor: {
      type: String,
      required: false,
      default: () => null,
    },
    instructorAtTheEnd: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    showAddedDate: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    displayDuration: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    displayLabType: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    displayInstructor: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    displayParentCourse: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    courseName: {
      type: String,
      required: false,
      default: () => '',
    },
    displayRunningPill: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    running: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    showHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
    displayVendor: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState({
      searchUserStatusBootcamps: state =>
        state.bootcamps.searchUserStatusBootcamps,
    }),
    maxLength() {
      if (this.getBreakpoint() === 'mobile') {
        return 10
      }
      return 90
    },
    userStatus() {
      const itemStatus = this.searchUserStatusBootcamps?.find(
        e => e.id === this.course.objectID
      )
      return itemStatus?.user_status
    },
    registered() {
      return this.userStatus?.registered
    },
    resultId() {
      return this.course?.slug
    },
    lastActivity() {
      return (
        (this.course?.last_activity_date &&
          this.course?.last_activity_date !== 'None') ||
        this.userStatus?.last_activity_date
      )
    },
    itemProgress() {
      if (this.progress) return this.progress
      let progress = 0
      if (this.userStatus && this.userStatus.progress) {
        progress = Math.floor(this.userStatus.progress * 100)
      }
      return progress
    },
    isContentComingSoon() {
      const contentComingSoonKey = CONSTANTS.BOOTCAMP_CONTENT_STATUS.CONTENT_COMING_SOON.replace(
        /_/g,
        ' '
      )
      return this.course.status
        ? this.course.status === contentComingSoonKey
        : false
    },
    showDuration() {
      return this.displayDuration && this.itemDuration() !== ''
    },
  },
  methods: {
    ...mapMutations({
      setParentContent: 'content/CURRENT_PARENT',
    }),
    getLearningAreaColor(course) {
      return `background: ${getLearningAreaSnipeColor(getLearningArea(course))}`
    },
    showLastActivity() {
      return this.lastActivity && this.lastActivity !== 'None'
    },
    getTimeInDaysAgo() {
      const time = this.lastActivity ?? ''
      return time && time !== 'None'
        ? `${formatDistance(new Date(), new Date(time))} ago`
        : ''
    },
    getInstructorNameText(course) {
      let text = getEntities.getInstructorName(course)
      const amountOfInstructors = getEntities.getAmountOfInstructors(course)
      if (amountOfInstructors > 1) {
        text += ` +${amountOfInstructors}`
      }
      return text
    },
    getLabTypeLabel(item) {
      if (!item.lab_type) {
        return null
      }
      const types = {
        azure: 'Azure',
        github: 'GitHub',
        els: 'eLearningSecurity',
        pta: 'Pentester Academy',
      }
      return types[item.lab_type]
    },
    truncateTitle(value, length = null) {
      return truncateText(value, !length ? this.maxLength : length)
    },
    getVendor(item) {
      return getEntities.getVendorName(item)
    },
    handleVideoEmit(video) {
      this.setParentContent({
        id: video.parent_id,
        type: video.parent_type,
      })
      this.$emit('open-video', video)
    },
    savePosition() {
      saveCurrentElement(this.resultId)
      if (this.type === CONSTANTS.CONTENT_TYPES.VIDEO)
        this.handleVideoEmit(this.course)
    },
    itemDuration() {
      if (this.duration && this.duration !== '') return this.duration
      if (this.course.duration_in_seconds) {
        return duration.durationFromSecs(this.course.duration_in_seconds)
      }
      if (this.course.duration) {
        const duration_in_seconds = duration.convertToSeconds(
          this.course.duration
        )
        return duration.durationFromSecs(duration_in_seconds)
      }
      return ''
    },
  },
  mounted() {
    this.$emit('el-rend', this.resultId)
  },
}
</script>

<style lang="scss">
.list-result-style {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  color: var(--white);
  font-family: var(--font-book);
  display: flex;
  width: 100%;
  height: 100%;
  background: var(--glass-mid-gray);
  max-height: 106px;

  &__certificate {
    color: var(--white);
    display: flex;
    align-items: center;
  }
  &__certificate-icon {
    width: 18px;
    height: 18px;
    svg {
      width: 8px;
      height: 10px;
    }
    @include mobile {
      width: 20px;
      height: 20px;
      svg {
        width: 10px;
        height: 12px;
      }
    }
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 5px;
  }
  &:hover {
    background: var(--glass-mid-gray-light);
  }
  &:active {
    background: var(--glass-dark);
  }
  &__icon-wrapper {
    display: flex;
    flex-wrap: wrap;
    align-content: center;
    justify-content: center;
    flex-grow: 1;
  }
  &__icon {
    font-size: 3rem;
  }
  &__progress-bar-wrapper {
    padding: 0 8px 8px 8px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
  &__vendor-wrapper {
    display: flex;
    align-items: center;
  }
  &__vendor-dot {
    width: 8px;
    height: 8px;
    border-radius: 4px;
    margin-right: 5px;
    @include mobile {
      &::after {
        display: none;
      }
    }
  }
  &__info {
    width: calc(100% - 116px);
    height: 100%;
    padding: 10px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    @include mobile {
      padding: 10px 10px 10px 30px;
      justify-content: center;
      flex-direction: column;
      display: flex;
    }
  }
  &__header {
    background: var(--glass-pink-violet);
    border-radius: 10px 0px 0px 0px;
    display: flex;
    justify-content: center;

    font-size: 10px;
    line-height: 12px;

    @include mobile {
      font-size: 12px;
      line-height: 14px;
    }

    &-description {
      display: none;

      @include mobile {
        display: block;
        min-height: 22px;
        padding: 5px 0px;
      }
    }

    &-status {
      min-height: 22px;
      padding: 5px 0px;

      @include mobile {
      }
    }
  }
  &__tile-img {
    display: flex;
    flex-direction: column;
    position: relative;
    min-width: 116px;
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    background-repeat: no-repeat;
    background-position: right;
    background-size: 280%;

    @include tablet {
      width: 135px;
      background-size: 185%;
    }
    @include mobile {
      width: 135px;
      background-size: 185%;
    }
    & img {
      width: 100%;
      height: 100%;
    }
  }
  &__title {
    font-size: 12px;
    font-family: var(--font-bold);
    padding-top: 10px;
    width: 100%;
    @include mobile {
      font-size: 1rem;
      padding-top: 0;
    }
  }
  &__top {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    &-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    @include mobile {
    }
  }
  &__bottom-instructor {
    display: flex;
  }
  &__bottom-duration-wrapper {
    display: flex;
    .bottom-duration .bottom-lastactivity {
      white-space: break-spaces;
    }
  }
  &--registered {
    font-size: 12px;
    color: var(--positive-light);
    width: 100%;
  }
  &__difficulty {
    font-size: 10px;
    text-transform: capitalize;
    @include mobile {
      font-size: 12px;
    }
  }
  &__bottom {
    font-size: 10px;
    justify-content: flex-start;
    flex-wrap: wrap;
    display: flex;
    @include mobile {
      font-size: 12px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin-top: 5px;
    }
    .bottom-element {
      white-space: nowrap;
      display: flex;
      align-items: center;
      &::after {
        content: '';
        display: block;
        background: var(--perfect-gray);
        width: 1px;
        height: 14px;
        margin: 0 5px;
      }
      @include mobile {
        &::after {
          content: '';
          display: block;
          background: var(--perfect-gray);
          width: 1px;
          height: 14px;
          margin: 0 10px;
        }
      }
      &:last-child {
        &::after {
          display: none;
        }
      }
    }
  }
  &__snipe {
    text-transform: uppercase;
  }
}
</style>
